/**
 * The display rules of a results grid's content slots (ED-327).
 *
 * Where a slot sits is not this element's business any more: the grid lays
 * itself out by `order` (snippets/ia-grid-order, ia-collection.css), each
 * product cell and each slot carrying its own number from Liquid, so the
 * first paint is already right and two items can never share a cell. What
 * Liquid cannot see is the rendered result, and two rules read that:
 *
 * - a grid slot never sits on the row right above or below a landscape slot
 *   (the two would read as one stacked block): it moves past the row of
 *   products below the landscape;
 * - a landscape slot with no product below it would close the grid, and a
 *   grid slot with no product on its row or below it would sit alone on the
 *   last row: either is left out until a later batch puts products there.
 *
 * The third rule, no landscape in a collection under 6 products, is Liquid's
 * (ia-col-fallback).
 */

import { EVENTS, subscribe } from 'ia-util-pubsub';

// The wide polaroid slot (ia-col-banner, variant collection; ia-col-slot-v2
// landscape). A marquee is a full-row band too, but a strip of text, not a
// slot: a tile right under it is the design, not a stacked block.
const LANDSCAPE = '.ia-col-banner--collection, .ia-col-slot--landscape';
// Every full-row band, marquee included: none may close the grid.
const BAND = 'ia-collection__promo--big';
// The product-sized tile (ia-col-banner interruptor, ia-col-slot-v2 grid).
const GRID_SLOT = '.ia-collection__promo--small, .ia-col-slot--grid';

class IaGridGuard extends HTMLElement {
  connectedCallback() {
    this._run = () => this.sweep();
    // The deadline keeps a background tab (no idle time ever comes) from
    // holding a swapped grid at its plain cells until the shopper returns.
    this._queue = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(this._run, { timeout: 1000 });
      } else {
        setTimeout(this._run, 1);
      }
    };
    // First pass as soon as the grid is parsed (a module runs after the
    // parse); the pass after load measures again once images and the async
    // stylesheets have set the real heights.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this._run, { once: true });
    } else {
      this._run();
    }
    if (document.readyState !== 'complete') {
      window.addEventListener('load', this._queue, { once: true });
    }
    // Columns differ per viewport, so a crossing of the breakpoint renders a
    // layout this sweep has not seen yet.
    this._onResize = () => {
      clearTimeout(this._timer);
      this._timer = setTimeout(this._run, 200);
    };
    window.addEventListener('resize', this._onResize);
    // A filter, sort or load-more swaps the grid for fresh markup; the idle
    // queue runs after the swap the same event triggers.
    this._unsubscribe = subscribe(EVENTS.filtersUpdate, this._queue);
    // A batch revealed in place changes what sits below every slot.
    this._unsubscribeReveal = subscribe(EVENTS.productsRendered, this._queue);
  }

  disconnectedCallback() {
    document.removeEventListener('DOMContentLoaded', this._run);
    window.removeEventListener('load', this._queue);
    window.removeEventListener('resize', this._onResize);
    this._unsubscribe?.();
    this._unsubscribeReveal?.();
    clearTimeout(this._timer);
  }

  grid() {
    return this.closest('.shopify-section, section')?.querySelector('[data-ia-product-grid]');
  }

  sweep() {
    const grid = this.grid();
    if (!grid) return;
    // Direct children only: a slot's inner markup is not a grid item. A slot
    // an earlier pass moved or left out starts again from its stylesheet.
    const promos = Array.from(grid.querySelectorAll(':scope > .ia-collection__promo'));
    promos.forEach((el) => {
      el.style.order = '';
      if (el.style.display === 'none') el.style.display = '';
    });
    const shown = promos.filter((el) => el.offsetParent !== null);
    // A hidden pane measures everything at zero.
    if (!shown.length || grid.clientWidth === 0) return;
    const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
    if (!cols) return;

    this.separateFromLandscapes(shown, grid, cols);
    this.dropClosingSlots(shown, grid);
  }

  /* Rows read off the rendered tops: auto-placed items report no row of
     their own, and the items of one row share a top. */
  rowIndexer(grid) {
    const tops = Array.from(grid.children)
      .filter((el) => el.offsetParent !== null)
      .map((el) => Math.round(el.getBoundingClientRect().top))
      .filter((top, index, all) => all.indexOf(top) === index)
      .sort((a, b) => a - b);
    return (el) => {
      const top = Math.round(el.getBoundingClientRect().top);
      return tops.findIndex((candidate) => Math.abs(candidate - top) <= 1);
    };
  }

  /* A grid slot on the row right above or below a landscape moves past the
     row of products below it: the landscape's number plus one row's worth.
     Each move clears one landscape for that slot for good, so the two counts
     bound the walk. */
  separateFromLandscapes(promos, grid, cols) {
    const landscapes = promos.filter((el) => el.matches(LANDSCAPE));
    const slots = promos.filter((el) => el.matches(GRID_SLOT));
    if (!landscapes.length || !slots.length) return;
    const limit = slots.length * (landscapes.length + 1);
    for (let step = 0; step < limit; step += 1) {
      const rowOf = this.rowIndexer(grid);
      const moved = slots.some((slot) => {
        const row = rowOf(slot);
        const beside = landscapes.find((land) => Math.abs(rowOf(land) - row) === 1);
        if (!beside) return false;
        const order = parseInt(getComputedStyle(beside).order, 10) || 0;
        slot.style.order = String(order + cols * 10);
        return true;
      });
      if (!moved) break;
    }
  }

  /* A landscape slot with no product below it would close the grid, and a
     grid slot with no product on its row or below it would sit alone on the
     last row: either is left out until a later batch puts products there. */
  dropClosingSlots(promos, grid) {
    const products = Array.from(grid.querySelectorAll(':scope > :not(.ia-collection__promo)'))
      .filter((el) => el.offsetParent !== null)
      .map((el) => el.getBoundingClientRect());
    promos.forEach((el) => {
      const landscape = el.classList.contains(BAND);
      if (!landscape && !el.matches(GRID_SLOT)) return;
      const rect = el.getBoundingClientRect();
      const followed = products.some((product) =>
        landscape ? product.top >= rect.bottom - 1 : product.bottom > rect.top + 1);
      if (!followed) el.style.display = 'none';
    });
  }
}

if (!customElements.get('ia-grid-guard')) {
  customElements.define('ia-grid-guard', IaGridGuard);
}
